import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { CustomDrawer } from 'components/Common/CustomDrawer/Index';
import {
  ScribeDeleteIcon,
  ScribeEditIcon,
  ScribeModalDeleteIcon,
} from 'components/Icons/ScribeIcons';
import { MacroAddForm } from 'pages/Account/MacroAddForm';
import {
  useCreateTextMacro,
  useDeleteTextMacro,
  useUpdateTextMacro,
} from 'queries/Scribe';
import React, { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';

import { queryKeys } from '../../../constants';

const TextMacroManager = ({
  textMacros,
  setTextMacros,
  isTextMacroLoading,
}: {
  textMacros: { id: number; trigger: string; text: string }[];
  setTextMacros: React.Dispatch<
    React.SetStateAction<{ id: number; trigger: string; text: string }[]>
  >;
  isTextMacroLoading: boolean;
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editTrigger, setEditTrigger] = useState('');
  const [editText, setEditText] = useState('');
  const [newTrigger, setNewTrigger] = useState('');
  const [newText, setNewText] = useState('');
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [macroToDelete, setMacroToDelete] = useState<number | null>(null);
  const [editingMacro, setEditingMacro] = useState<any>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const scribeUserData: any = localStorage.getItem('scribeUserData') || '';

  const userRolesData = localStorage.getItem('roles') || '';
  const userData = userRolesData ? JSON.parse(userRolesData) : null;
  const userid = userData?.id;

  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const { mutate: createTextMacro } = useCreateTextMacro();
  const { mutate: updateTextMacro } = useUpdateTextMacro();
  const { mutate: deleteTextMacro } = useDeleteTextMacro();

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value.toLowerCase());
  };

  const [filteredMacros, setFilteredMacros] = useState(textMacros);

  useEffect(() => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();
    const filtered = textMacros.filter(
      (macro) =>
        macro.trigger.toLowerCase().includes(normalizedSearchTerm) ||
        macro.text.toLowerCase().includes(normalizedSearchTerm)
    );
    setFilteredMacros(filtered);
  }, [searchTerm, textMacros]);
  // console.log('Filtered Macros:', filteredMacros);
  // console.log('Search Term:', searchTerm);

  const handleDelete = (id: number) => {
    setDialogOpen(true);
    setMacroToDelete(id);
  };

  const onOpenChange = (open: any) => {
    setIsDrawerOpen(open);
  };

  const onEditChange = (open: any) => {
    setIsFormOpen(open);
  };

  const handleOpenDrawer = () => {
    setIsDrawerOpen(true);
  };

  const confirmDelete = async () => {
    if (macroToDelete !== null) {
      try {
        const token = localStorage.getItem('AUTH_TOKEN');
        const response = await fetch(
          `${baseUrl}/api/textmacros/${macroToDelete}`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ userid }),
          }
        );

        if (!response.ok) throw new Error('Failed to delete macro');

        if (setTextMacros) {
          setTextMacros((prev) =>
            prev.filter((macro) => macro.id !== macroToDelete)
          );
        }

        setToast({
          open: true,
          message: 'Text macro deleted successfully!',
          severity: 'success',
        });
      } catch (error) {
        console.error('Error deleting macro:', error);
        setToast({
          open: true,
          message: 'Failed to delete text macro. Please try again.',
          severity: 'error',
        });
      }

      setDialogOpen(false);
      setMacroToDelete(null);
    }
  };

  const cancelDelete = () => {
    setDialogOpen(false);
    setMacroToDelete(null);
  };

  const handleEdit = (macro: any) => {
    setEditingMacro({
      trigger: macro.trigger,
      description: macro.text,
      id: macro.id,
    });
    setIsFormOpen(true); // Open the form when editing
  };

  const handleSave = (id: number) => {
    setTextMacros((prev) =>
      prev.map((macro) =>
        macro.id === id
          ? { ...macro, trigger: editTrigger, text: editText }
          : macro
      )
    );
    const updatedMacros = textMacros.map((macro) =>
      macro.id === id
        ? { ...macro, trigger: editTrigger, text: editText }
        : macro
    );

    setTextMacros(updatedMacros); // Update parent state
    setEditingId(null); // Exit edit mode
    updateTextMacro(
      {
        id,
        text: editText,
        trigger: editTrigger,
        patient_id: scribeUserData?.patient_id,
      },
      {
        onSuccess: () => {
          setToast({
            open: true,
            message: 'Text macro updated successfully!',
            severity: 'success',
          });
          queryClient.invalidateQueries([queryKeys.SCRIBE_TEXT_MACRO]);
        },
        onError: () => {
          setToast({
            open: true,
            message: 'Failed to updated text macro. Please try again.',
            severity: 'error',
          });
        },
      }
    );
  };

  const handleCancel = () => {
    setEditingId(null); // Exit edit mode without saving
  };

  const handleAddMacro = () => {
    if (!newTrigger || !newText) {
      setToast({
        open: true,
        message: 'Please enter both Trigger Name and Description',
        severity: 'error',
      });
      return;
    }

    const newMacro = {
      id: Math.max(0, ...textMacros.map((macro) => macro.id)) + 1, // Generate a unique ID
      trigger: newTrigger,
      text: newText,
    };

    setTextMacros((prev) => [...prev, newMacro]);
    setNewTrigger(''); // Reset input fields
    setNewText('');

    createTextMacro(
      {
        text: newText,
        trigger: newTrigger,
        patient_id: scribeUserData?.patient_id,
      },
      {
        onSuccess: () => {
          setToast({
            open: true,
            message: 'Text macro created successfully!',
            severity: 'success',
          });
          queryClient.invalidateQueries([queryKeys.SCRIBE_TEXT_MACRO]);
        },
        onError: () => {
          setToast({
            open: true,
            message: 'Failed to create text macro. Please try again.',
            severity: 'error',
          });
        },
      }
    );
  };

  const handleCloseToast = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  if (isTextMacroLoading)
    return (
      <Box>
        <div className="p-2 px-3">
          <Typography
            variant="h6"
            sx={{ marginBottom: 2 }}
            className="font-semibold"
          >
            Text Macro Manager
          </Typography>
        </div>
        <div className="flex justify-center pt-11">
          <CircularProgress />
        </div>
      </Box>
    );

  return (
    <div className="flex flex-col justify-between ">
      {/* Title */}
      <div className="p-2 px-3">
        <Typography
          variant="h6"
          sx={{ marginBottom: 2 }}
          className="font-semibold"
        >
          Text Macro Manager
        </Typography>

        {/* Search Input */}
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: 2,
              border: '1px solid #ececec',
              borderRadius: 1,
              paddingX: 1,
            }}
          >
            <SearchIcon sx={{ color: '#6b7280' }} />
            <TextField
              fullWidth
              placeholder="Search"
              size="small"
              variant="standard"
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                disableUnderline: true,
              }}
            />
          </Box>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleOpenDrawer}
            className="text-sm"
            sx={{
              alignSelf: 'flex-start',
              backgroundColor: '#11AC9E',
              border: '1px solid #e0e0e0',
              color: '#fff',
              textTransform: 'none',
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: '#90BEE6',
                borderColor: '#d1d5db',
              },
            }}
            startIcon={<AddIcon className="mr-2 h-5 w-5 fill-white" />}
          >
            Add Macro
          </Button>
        </div>

        {isDrawerOpen && (
          <CustomDrawer heading="New Macro" onOpenChange={onOpenChange}>
            <MacroAddForm
              onOpenChange={onOpenChange}
              setToast={setToast}
              setTextMacros={setTextMacros}
            />
          </CustomDrawer>
        )}

        {/* Macro List */}
        <div className="w-full overflow-y-auto py-2 pr-2">
          {filteredMacros.length > 0 ? (
            filteredMacros.map((macro: any) => {
              return (
                <Box
                  key={macro.id}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    paddingY: 1,
                    borderBottom: '1px solid #ececec',
                    gap: 2,
                    position: 'relative',
                  }}
                >
                  {editingId === macro.id ? (
                    // Editable State
                    <div className="flex items-start justify-between gap-1">
                      <div className="flex w-full flex-col gap-2">
                        <TextField
                          fullWidth
                          size="small"
                          className="text-sm"
                          value={editTrigger}
                          InputProps={{
                            sx: { padding: 0, fontSize: '0.9rem' },
                          }}
                          onChange={(e) => setEditTrigger(e.target.value)}
                          sx={{
                            backgroundColor: '#fff',
                            borderRadius: 1,
                            maxWidth: '300px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          multiline
                          className="text-sm"
                          rows={2}
                          value={editText}
                          InputProps={{
                            sx: { padding: 0, fontSize: '0.9rem' },
                          }}
                          onChange={(e) => setEditText(e.target.value)}
                          sx={{
                            backgroundColor: '#fff',
                            borderRadius: 1,
                            maxWidth: '300px',
                          }}
                        />
                      </div>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 0.5,
                          position: 'absolute',
                          top: 5,
                          right: 1,
                          flexDirection: 'column',
                        }}
                      >
                        <Tooltip title="Save" placement="top">
                          <IconButton
                            size="small"
                            aria-label="save"
                            onClick={() => handleSave(macro.id)}
                            sx={{
                              backgroundColor: '#4caf50',
                              color: '#fff',
                              '&:hover': { backgroundColor: '#388e3c' },
                            }}
                          >
                            <CheckIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Discard Changes">
                          <IconButton
                            size="small"
                            aria-label="cancel"
                            onClick={handleCancel}
                            sx={{
                              backgroundColor: '#f44336',
                              color: '#fff',
                              '&:hover': { backgroundColor: '#d32f2f' },
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </div>
                  ) : (
                    // Non-Editable State
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        position: 'relative',
                      }}
                    >
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, color: '#101828' }}
                        >
                          {macro.trigger}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {macro.text}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'start',
                          // position: 'absolute',
                          // top: 5,
                          // right: 1,
                        }}
                      >
                        <Tooltip title="Edit Macro">
                          <IconButton
                            size="small"
                            aria-label="edit"
                            onClick={() => handleEdit(macro)}
                          >
                            <ScribeEditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Macro">
                          <IconButton
                            size="small"
                            aria-label="delete"
                            onClick={() => handleDelete(macro.id)}
                          >
                            <ScribeDeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  )}
                </Box>
              );
            })
          ) : (
            <Typography variant="body2" color="textSecondary" align="center">
              No macros found.
            </Typography>
          )}
        </div>
      </div>

      {isFormOpen && (
        <CustomDrawer heading="Edit Macro" onOpenChange={onEditChange}>
          <MacroAddForm
            onOpenChange={setIsFormOpen}
            setToast={setToast}
            initialValues={editingMacro} // Pass the macro data to the form for editing
            isEditMode={true} // Indicate this is the edit mode
            setTextMacros={setTextMacros}
          />
        </CustomDrawer>
      )}
      {/* Snackbar */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseToast}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={cancelDelete}
        maxWidth={false}
        fullWidth={false}
        sx={{ '& .MuiDialog-paper': { width: '400px' } }}
      >
        <DialogTitle className="justify-center-center flex justify-between gap-2">
          <div className="flex items-center justify-between">
            <ScribeModalDeleteIcon />
            <div className="font-semibold ">Delete Macro</div>
          </div>
          <IconButton
            onClick={cancelDelete}
            sx={{ color: 'grey.600' }}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText className="text-md">
            Are you sure you want to delete this macro? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions className="flex items-center justify-end gap-3">
          <Button
            onClick={cancelDelete}
            color="info"
            variant="outlined"
            className="w-36 text-black"
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            className="w-36 "
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TextMacroManager;
